// src/pages/sessions/CreateSession.tsx
import { logger } from '../../utils/logger';
// ✅ IMPROVED: End time now toggleable, better layout

import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonBackButton,
  IonList,
  IonListHeader,
  IonItem,
  IonLabel,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonDatetime,
  IonSpinner,
  IonToast,
  IonAlert,
  IonNote,
  IonToggle,
  IonCard,
  IonCardContent,
  IonIcon,
} from '@ionic/react';
import { informationCircle, sparkles } from 'ionicons/icons';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { sessionService } from '../../services/sessionService';
import { canCreateSession, isPremium } from '../../services/subscriptionService';
import { moderateContent } from '../../services/contentModeration';
import { PremiumPaywall } from '../../components/PremiumPaywall';
import { LocationType } from '../../types/session';
import { updateDataWithRetry } from '../../services/retryService';
import { handleError, ErrorMessages } from '../../services/errorHandler';
import './CreateSession.css';

const CreateSession: React.FC = () => {
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  const [userIsPremium, setUserIsPremium] = useState(false);

  // Form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [setEndTimeEnabled, setSetEndTimeEnabled] = useState(false);
  const [locationType, setLocationType] = useState<LocationType>('in-person');
  const [locationDetails, setLocationDetails] = useState('');
  const [platform, setPlatform] = useState('');
  const [maxPlayers, setMaxPlayers] = useState<number | undefined>(undefined);
  const [setMaxPlayersEnabled, setSetMaxPlayersEnabled] = useState(false);

  useEffect(() => {
    const checkLimits = async () => {
      const premium = await isPremium();
      setUserIsPremium(premium);

      const { allowed, reason } = await canCreateSession();

      if (!allowed && reason) {
        logger.log('Session limit reached:', reason);
      }
    };

    checkLimits();
  }, []);

  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      setErrorMessage('Please enter a session title');
      setShowError(true);
      return;
    }

    if (!sessionDate) {
      setErrorMessage('Please select a session date');
      setShowError(true);
      return;
    }

    if (!startTime) {
      setErrorMessage('Please select a start time');
      setShowError(true);
      return;
    }

    if (locationType === 'in-person' && !locationDetails.trim()) {
      setErrorMessage('Please enter location details for in-person sessions');
      setShowError(true);
      return;
    }

    if (locationType === 'online' && !platform.trim()) {
      setErrorMessage('Please specify the online platform');
      setShowError(true);
      return;
    }

    // Content moderation
    if (locationType === 'online') {
      const platformCheck = moderateContent(platform.trim());
      if (!platformCheck.isClean) {
        setErrorMessage(platformCheck.reason || 'Platform name contains inappropriate content');
        setShowError(true);
        return;
      }
    }

    // Re-check session limit before creating
    const { allowed, reason } = await canCreateSession();
    if (!allowed) {
      setErrorMessage(reason || 'You have reached your session limit');
      setShowPaywall(true);
      return;
    }

    setSaving(true);

    const formattedDate = sessionDate.split('T')[0];

    // Extract time from datetime string (format: HH:mm)
    const extractTime = (datetimeStr: string) => {
      if (datetimeStr.includes('T')) {
        return datetimeStr.split('T')[1].substring(0, 5);
      }
      return datetimeStr;
    };

    try {
      const result = await updateDataWithRetry(
        () => sessionService.createSession({
          title: title.trim(),
          description: description.trim() || undefined,
          session_date: formattedDate,
          start_time: extractTime(startTime),
          end_time: (setEndTimeEnabled && endTime) ? extractTime(endTime) : undefined,
          location_type: locationType,
          location_details: locationType === 'in-person' ? locationDetails.trim() : undefined,
          platform: locationType === 'online' ? platform.trim() : undefined,
          max_players: setMaxPlayersEnabled ? maxPlayers : undefined,
        })
      );

      setSaving(false);

      if (result.success && result.data?.session) {
        setToastMessage('Session created successfully!');
        setShowToast(true);
        setTimeout(() => {
          history.replace(`/tabs/sessions/${result.data!.session!.id}`);
        }, 1000);
      } else {
        const errorMsg = result.error?.userMessage || 'Failed to create session. Please try again.';
        setErrorMessage(errorMsg);
        setShowError(true);
      }
    } catch (error) {
      setSaving(false);
      const appError = handleError(error, 'create session');
      setErrorMessage(appError.userMessage);
      setShowError(true);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/sessions" />
          </IonButtons>
          <IonTitle>Create Session</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSubmit} disabled={saving}>
              {saving ? <IonSpinner /> : 'Create'}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="create-session-container">
          {/* Free User Notice */}
          {!userIsPremium && (
            <IonCard color="light">
              <IonCardContent>
                <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                  <IonIcon
                    icon={informationCircle}
                    style={{ fontSize: '24px', flexShrink: 0, marginTop: '2px' }}
                    color="primary"
                  />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>Free Account Limit</p>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                      You can host <strong>1 active session at a time</strong>. Once it's completed, you can create another!
                    </p>
                    <IonButton
                      size="small"
                      fill="clear"
                      onClick={() => setShowPaywall(true)}
                      style={{ margin: 0, padding: 0, height: 'auto' }}
                    >
                      <IonIcon icon={sparkles} slot="start" />
                      Upgrade for Unlimited Sessions
                    </IonButton>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>
          )}

          {/* Basic Information */}
          <IonList>
            <IonListHeader>
              <IonLabel>Basic Information</IonLabel>
            </IonListHeader>

            <IonItem>
              <IonLabel position="stacked">Session Title *</IonLabel>
              <IonInput
                value={title}
                onIonInput={(e) => setTitle(e.detail.value!)}
                placeholder="Friday Night Games"
                maxlength={100}
                autocapitalize="sentences"
              />
              <IonNote slot="helper" color="warning">
                ⚠️ Title cannot be changed after creation
              </IonNote>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Description</IonLabel>
              <IonTextarea
                value={description}
                onIonInput={(e) => setDescription(e.detail.value!)}
                placeholder="Casual game night, bring your favorites!"
                rows={3}
                maxlength={500}
                autocapitalize="sentences"
              />
              <IonNote slot="helper">{description.length}/500</IonNote>
            </IonItem>
          </IonList>

          {/* Date & Time */}
          <IonList>
            <IonListHeader>
              <IonLabel>When</IonLabel>
            </IonListHeader>

            <IonItem lines="none">
              <IonLabel position="stacked">Date *</IonLabel>
              <IonDatetime
                value={sessionDate}
                onIonChange={(e) => setSessionDate(e.detail.value as string)}
                presentation="date"
                min={new Date().toISOString()}
                preferWheel={true}
              />
            </IonItem>

            <IonItem lines="none">
              <IonLabel position="stacked">Start Time *</IonLabel>
              <IonDatetime
                value={startTime}
                onIonChange={(e) => setStartTime(e.detail.value as string)}
                presentation="time"
                preferWheel={true}
              />
            </IonItem>

            <IonItem>
              <IonLabel>Set End Time</IonLabel>
              <IonToggle
                checked={setEndTimeEnabled}
                onIonChange={(e) => {
                  setSetEndTimeEnabled(e.detail.checked);
                  if (!e.detail.checked) setEndTime('');
                }}
              />
            </IonItem>

            {setEndTimeEnabled && (
              <IonItem lines="none">
                <IonLabel position="stacked">End Time</IonLabel>
                <IonDatetime
                  value={endTime}
                  onIonChange={(e) => setEndTime(e.detail.value as string)}
                  presentation="time"
                  preferWheel={true}
                />
              </IonItem>
            )}
          </IonList>

          {/* Location */}
          <IonList>
            <IonListHeader>
              <IonLabel>Where</IonLabel>
            </IonListHeader>

            <IonItem>
              <IonLabel>Location Type *</IonLabel>
              <IonSelect
                value={locationType}
                onIonChange={(e) => setLocationType(e.detail.value)}
              >
                <IonSelectOption value="in-person">In-Person</IonSelectOption>
                <IonSelectOption value="online">Online</IonSelectOption>
              </IonSelect>
            </IonItem>

            {locationType === 'in-person' ? (
              <IonItem>
                <IonLabel position="stacked">Location Details *</IonLabel>
                <IonInput
                  value={locationDetails}
                  onIonInput={(e) => setLocationDetails(e.detail.value!)}
                  placeholder="123 Main St, Coffee Shop, etc."
                  maxlength={200}
                  autocapitalize="sentences"
                />
              </IonItem>
            ) : (
              <IonItem>
                <IonLabel position="stacked">Platform *</IonLabel>
                <IonInput
                  value={platform}
                  onIonInput={(e) => setPlatform(e.detail.value!)}
                  placeholder="Discord, Tabletop Simulator, etc."
                  maxlength={100}
                  autocapitalize="sentences"
                />
              </IonItem>
            )}
          </IonList>

          {/* Player Limit */}
          <IonList>
            <IonListHeader>
              <IonLabel>Player Limit (Optional)</IonLabel>
            </IonListHeader>

            <IonItem>
              <IonLabel>Set Maximum Players</IonLabel>
              <IonToggle
                checked={setMaxPlayersEnabled}
                onIonChange={(e) => {
                  setSetMaxPlayersEnabled(e.detail.checked);
                  if (!e.detail.checked) setMaxPlayers(undefined);
                }}
              />
            </IonItem>

            {setMaxPlayersEnabled && (
              <IonItem>
                <IonLabel position="stacked">Maximum Players</IonLabel>
                <IonInput
                  type="number"
                  value={maxPlayers}
                  onIonInput={(e) => setMaxPlayers(parseInt(e.detail.value!, 10))}
                  placeholder="4"
                  min="2"
                  max="20"
                />
              </IonItem>
            )}
          </IonList>

          {/* Info Note */}
          <div className="info-note">
            <p><strong>After creating your session:</strong></p>
            <ul>
              <li>You can invite players from your connections</li>
              <li>A group chat will be created automatically</li>
              <li>Participants can suggest games to play</li>
              <li>You can edit the date, time, and location anytime</li>
              <li><em>Note: The session title cannot be changed</em></li>
            </ul>
          </div>
        </div>

        {/* Success Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          color="success"
          aria-live="polite"
        />

        {/* Error Alert */}
        <IonAlert
          isOpen={showError}
          onDidDismiss={() => setShowError(false)}
          header="Error"
          message={errorMessage}
          buttons={[
            {
              text: 'OK',
              role: 'cancel',
            },
            ...(errorMessage.includes('limit') ? [{
              text: 'Upgrade to Premium',
              handler: () => setShowPaywall(true),
            }] : []),
          ]}
        />

        {/* Premium Paywall */}
        <PremiumPaywall
          isOpen={showPaywall}
          onDismiss={() => setShowPaywall(false)}
          feature="sessions"
        />
      </IonContent>
    </IonPage>
  );
};

export default CreateSession;